/**
 * Controls for two thumbs.
 *
 * Screen space is the scarce resource on a phone: every control covers pixels
 * you are trying to shoot through. So there are five, each large — a stick, a
 * trigger, and three small buttons — and no crouch, no lean, no separate aim
 * mode.
 *
 * Three decisions carry most of the feel:
 *
 * The stick spawns under your thumb rather than living in a fixed corner, and
 * once your thumb passes the rim the origin follows it. A fixed stick runs out
 * of travel mid-turn and you have to lift and re-place; an origin-following
 * one never does.
 *
 * The trigger doubles as the aim origin. Press it and you fire; drag from it
 * and you fire while aiming, one gesture. But it is a real hit target rather
 * than "the whole right half fires", because that version spends a bullet
 * every time you look around. Touch anywhere else on the right and you aim
 * for free.
 *
 * And nothing here sniffs the user agent. Touchscreen laptops and phones with
 * bluetooth mice both exist, so the mode switches on the input actually used.
 */

export type InputMode = 'touch' | 'desktop';

export interface TouchState {
  /** -1..1 on each axis from the stick; +y is forward. */
  moveX: number;
  moveY: number;
  /** Look delta accumulated since the last frame, in pixels. */
  lookX: number;
  lookY: number;
  /** True while the trigger is held. */
  firing: boolean;
}

interface Finger {
  id: number;
  role: 'move' | 'look' | 'fire';
  /** Live origin of the stick — it slides when the thumb outruns it. */
  ox: number;
  oy: number;
  x: number;
  y: number;
  startedAt: number;
  travelled: number;
  /** Look fingers that began on the trigger also fire. */
  onTrigger: boolean;
  /** Which control it went down on, so lifting one does not un-light another. */
  button: string | null;
}

const STICK_RADIUS = 58;

interface Button {
  el: HTMLElement;
  name: 'trigger' | 'triggerleft' | 'reload' | 'jump' | 'swap';
}

export class TouchControls {
  readonly state: TouchState = { moveX: 0, moveY: 0, lookX: 0, lookY: 0, firing: false };
  /** Which input the player last actually used. */
  mode: InputMode;
  onMode: ((m: InputMode) => void) | null = null;
  /** One-shot presses, drained by the game loop. */
  private pressed = { jump: false, reload: false, swap: false, fireEdge: false };

  private fingers = new Map<number, Finger>();
  private root: HTMLElement;
  private layer: HTMLElement;
  private stick: HTMLElement;
  private knob: HTMLElement;
  private buttons: Button[] = [];
  private sensitivity: number;

  constructor(root: HTMLElement, sensitivity = 0.0034) {
    this.root = root;
    this.sensitivity = sensitivity;
    // An initial guess only; the first real input decides.
    /*
     * Ask whether there is a thumb, not whether there is no mouse.
     *
     * `!(pointer: fine)` is false on anything that also has a trackpad, and a
     * phone browser in desktop-site mode can claim one — so a phone could open
     * in the desktop dress and be told to press W, A, S and D. Either signal
     * being present is enough; the first real input corrects it anyway.
     */
    const coarse = matchMedia('(pointer: coarse)').matches
      || !matchMedia('(pointer: fine)').matches
      || navigator.maxTouchPoints > 0;
    this.mode = coarse ? 'touch' : 'desktop';

    this.layer = document.createElement('div');
    this.layer.className = 'tc';

    this.stick = document.createElement('div');
    this.stick.className = 'tc-stick';
    this.knob = document.createElement('div');
    this.knob.className = 'tc-knob';
    this.stick.append(this.knob);
    this.stick.style.display = 'none';
    this.layer.append(this.stick);

    // The trigger is inset from the edge so it has drag room in every
    // direction; the small buttons hug the corner beside it.
    for (const name of ['trigger', 'triggerleft', 'reload', 'jump', 'swap'] as const) {
      const el = document.createElement('div');
      el.className = `tc-btn tc-${name}`;
      // Glyphs, not letters. "R" is the name of a key on a keyboard that is
      // not there; a circling arrow is what the button does.
      el.textContent = name === 'trigger' || name === 'triggerleft' ? ''
        : name === 'reload' ? '⟳' : name === 'jump' ? '⤒' : '⇄';
      this.layer.append(el);
      this.buttons.push({ el, name });
    }

    root.append(this.layer);
    this.setMode(this.mode);

    root.addEventListener('pointerdown', this.onDown, { passive: false });
    root.addEventListener('pointermove', this.onMove, { passive: false });
    root.addEventListener('pointerup', this.onUp, { passive: false });
    root.addEventListener('pointercancel', this.onUp, { passive: false });
    addEventListener('keydown', this.onKey, { passive: true });
  }

  get lookSensitivity(): number {
    return this.sensitivity;
  }

  set lookSensitivity(v: number) {
    this.sensitivity = v;
  }

  private setMode(m: InputMode): void {
    if (this.mode !== m) this.mode = m;
    this.layer.style.display = m === 'touch' ? 'block' : 'none';
    if (m === 'desktop') {
      this.state.moveX = this.state.moveY = 0;
      this.state.firing = false;
      this.stick.style.display = 'none';
      this.fingers.clear();
    }
    this.onMode?.(m);
  }

  private onKey = (e: KeyboardEvent): void => {
    // A movement key means there is a keyboard, so stop drawing thumb controls.
    if (/^(Key[WASD]|Space|ShiftLeft)$/.test(e.code) && this.mode !== 'desktop') this.setMode('desktop');
  };

  /** Which button, if any, is under a point. */
  private hit(x: number, y: number): Button | null {
    for (const b of this.buttons) {
      const r = b.el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return b;
    }
    return null;
  }

  /**
   * Off while a screen is up.
   *
   * The thumb controls are drawn over the whole play surface, so a settings
   * panel or a card screen sits underneath the trigger. Hidden *and* deaf:
   * hiding alone leaves an invisible fire button over the resume button.
   */
  set active(on: boolean) {
    this.enabled = on;
    this.layer.style.display = on ? '' : 'none';
    if (!on) {
      this.fingers.clear();
      this.state.moveX = 0;
      this.state.moveY = 0;
      this.state.firing = false;
    }
  }

  private enabled = true;

  private onDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    if (e.pointerType === 'mouse') return;
    if (this.mode !== 'touch') this.setMode('touch');
    e.preventDefault();

    const btn = this.hit(e.clientX, e.clientY);
    if (btn && btn.name !== 'trigger' && btn.name !== 'triggerleft') {
      btn.el.classList.add('down');
      this.pressed[btn.name] = true;
      this.fingers.set(e.pointerId, {
        id: e.pointerId, role: 'look', ox: e.clientX, oy: e.clientY,
        x: e.clientX, y: e.clientY, startedAt: performance.now(), travelled: 0, onTrigger: false,
        button: btn.name,
      });
      return;
    }

    /*
     * Either trigger fires; only the right one is also an aim origin.
     *
     * The left one sits over the movement half, so treating it as a look
     * finger would take the stick away from the thumb that is on it. It fires
     * and does nothing else, which is the point of having it — it exists so
     * the right thumb can aim without also holding a trigger down.
     */
    const onTrigger = btn?.name === 'trigger';
    const onLeftTrigger = btn?.name === 'triggerleft';
    if (onLeftTrigger) {
      this.state.firing = true;
      this.pressed.fireEdge = true;
      btn!.el.classList.add('down');
      this.fingers.set(e.pointerId, {
        id: e.pointerId, role: 'fire', ox: e.clientX, oy: e.clientY,
        x: e.clientX, y: e.clientY, startedAt: performance.now(), travelled: 0, onTrigger: true,
        button: 'triggerleft',
      });
      return;
    }
    // Left of the divide moves, unless it is a button. Everything else aims.
    const isMove = !onTrigger && e.clientX < innerWidth * 0.42;
    if (isMove && [...this.fingers.values()].some((f) => f.role === 'move')) return;

    this.fingers.set(e.pointerId, {
      id: e.pointerId,
      role: isMove ? 'move' : 'look',
      ox: e.clientX, oy: e.clientY, x: e.clientX, y: e.clientY,
      startedAt: performance.now(), travelled: 0, onTrigger,
      button: onTrigger ? 'trigger' : null,
    });

    if (isMove) {
      this.stick.style.display = 'block';
      this.stick.style.left = `${e.clientX}px`;
      this.stick.style.top = `${e.clientY}px`;
      this.knob.style.transform = 'translate(-50%, -50%)';
    } else if (onTrigger) {
      this.state.firing = true;
      this.pressed.fireEdge = true;
      btn!.el.classList.add('down');
    }
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const f = this.fingers.get(e.pointerId);
    if (!f) return;
    e.preventDefault();
    const dx = e.clientX - f.x;
    const dy = e.clientY - f.y;
    f.travelled += Math.hypot(dx, dy);
    f.x = e.clientX;
    f.y = e.clientY;

    if (f.role === 'look') {
      this.state.lookX += dx;
      this.state.lookY += dy;
      return;
    }
    // A thumb on the left trigger is holding a trigger, not steering. Without
    // this it falls through to the stick below and walks you into a wall.
    if (f.role === 'fire') return;

    let ox = e.clientX - f.ox;
    let oy = e.clientY - f.oy;
    const len = Math.hypot(ox, oy);
    if (len > STICK_RADIUS) {
      // Origin-follow: drag the centre along behind the thumb so the stick
      // never runs out of travel however far you keep going.
      const pull = (len - STICK_RADIUS) / len;
      f.ox += ox * pull;
      f.oy += oy * pull;
      ox = e.clientX - f.ox;
      oy = e.clientY - f.oy;
      this.stick.style.left = `${f.ox}px`;
      this.stick.style.top = `${f.oy}px`;
    }
    this.state.moveX = ox / STICK_RADIUS;
    this.state.moveY = oy / STICK_RADIUS;
    this.knob.style.transform = `translate(calc(-50% + ${ox}px), calc(-50% + ${oy}px))`;
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const f = this.fingers.get(e.pointerId);
    if (!f) return;
    this.fingers.delete(e.pointerId);
    e.preventDefault();
    // Only the buttons nothing is still on: clearing them all meant lifting
    // one trigger un-lit the other while it was still held down.
    const held = new Set([...this.fingers.values()].map((x) => x.button));
    for (const b of this.buttons) if (!held.has(b.name)) b.el.classList.remove('down');

    if (f.role === 'move') {
      this.state.moveX = 0;
      this.state.moveY = 0;
      this.stick.style.display = 'none';
    } else if (f.onTrigger) {
      this.state.firing = [...this.fingers.values()].some((x) => x.onTrigger);
    }
  };

  /** Take and clear the accumulated look delta. */
  consumeLook(): { x: number; y: number } {
    const out = { x: this.state.lookX, y: this.state.lookY };
    this.state.lookX = 0;
    this.state.lookY = 0;
    return out;
  }

  /** Take and clear the one-shot presses. */
  consumePresses(): { jump: boolean; reload: boolean; swap: boolean; fireEdge: boolean } {
    const out = { ...this.pressed };
    this.pressed.jump = this.pressed.reload = this.pressed.swap = this.pressed.fireEdge = false;
    return out;
  }

  dispose(): void {
    this.root.removeEventListener('pointerdown', this.onDown);
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerup', this.onUp);
    this.root.removeEventListener('pointercancel', this.onUp);
    removeEventListener('keydown', this.onKey);
    this.layer.remove();
  }
}
